import { DestroyRef, effect, inject, Injectable, Signal } from '@angular/core';
import { FormArray, FormControlStatus, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { combineLatest, forkJoin, from, lastValueFrom, map, Observable, of, switchMap, zip } from 'rxjs';
import { DataMartService, SalesforceService } from '@r1-compass-library/services';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';
import { formCombined, Uuid } from '@r1-compass-library/utilities';
import { toObservable } from '@angular/core/rxjs-interop';
import { signalSlice } from 'ngxtension/signal-slice';
import {
    DataMartField,
    DataMartTable,
    DivisionConfig,
    DivisionConfigData,
    DivisionMappingSettings,
    DivisionSalesforceTargetSettings,
    DivisionTableRelationships,
    RequiredDivisionConfigData,
    SalesforceField,
    SalesforceFieldMetadata,
    SalesforceUniqueFieldTarget,
} from '@r1-compass-library/models';
import { DivisionSalesforceTargetsFormValidatorsService } from './division-salesforce-targets-form-validators.service';
import { DivisionSettingsFormSliceState, FormLifecycleService } from '../form-lifecycle.service';
import {
    DivisionSalesforceTargetsForm,
    DivisionSalesforceTargetsFormValue,
    UniqueSalesforceTargetFieldForm,
} from './form.model';

interface DivisionSalesforceTargetsFormSliceState
    extends DivisionSettingsFormSliceState<DivisionSalesforceTargetsFormValue> {
    dataMartFields: DataMartField[];
    dataMartTable: DataMartTable | undefined;
}

@Injectable()
export class DivisionSalesforceTargetsFormService {
    readonly #fb = inject(NonNullableFormBuilder);
    readonly #formLifecycleService = inject(FormLifecycleService);
    readonly #dataMartService = inject(DataMartService);
    readonly #salesforceService = inject(SalesforceService);
    readonly #validators = inject(DivisionSalesforceTargetsFormValidatorsService);
    readonly #destroyRef = inject(DestroyRef);
    readonly #globalDivisionStore = inject(GlobalDivisionsStore);
    readonly #globalLoggedInUserStore = inject(GlobalLoggedInUserStore);

    public divisionSalesforceTargetsForm: FormGroup<DivisionSalesforceTargetsForm> = this.#fb.group({
        salesforcePlayObject: this.#fb.control('', Validators.required),
        salesforceRecommendationObject: this.#fb.control('', Validators.required),
        salesforceUniqueRecommendationFieldTargetName: this.#fb.control('', Validators.required),
        uniqueSalesforceTargetFields: this.#fb.array<FormGroup<UniqueSalesforceTargetFieldForm>>([]),
    });

    private initialFormState: DivisionSalesforceTargetsFormSliceState = {
        value: this.divisionSalesforceTargetsForm.getRawValue(),
        status: 'PENDING',
        loaded: false,
        salesforceObjectOptions: [],
        recommendationFieldsOptions: undefined,
        selectedDivisionConfig: undefined,
        dataMartTableOptions: [],
        dataMartFields: [],
        dataMartTable: undefined,
    };

    private selectedDivisionConfig$ = toObservable<DivisionConfig | undefined>(
        this.#globalLoggedInUserStore.selectedDivisionConfig
    );
    private loggedInUserId: Signal<number | undefined> = this.#globalLoggedInUserStore.selectedUserId;

    public salesforceTargetsFormState = signalSlice({
        initialState: this.initialFormState,
        sources: [
            // As the form changes
            formCombined(this.divisionSalesforceTargetsForm),
            // On load/changes to the division
            // Useful for other config state on save
            this.selectedDivisionConfig$.pipe(map(config => ({ selectedDivisionConfig: config }))),
            state =>
                this.#formLifecycleService.initializeAndPatchForm(
                    state.selectedDivisionConfig$,
                    state.loaded$,
                    this.divisionSalesforceTargetsForm,
                    data =>
                        this.initializeEmptyMapperControls(
                            data,
                            this.divisionSalesforceTargetsForm.controls.uniqueSalesforceTargetFields,
                            this.#fb,
                            this.#validators
                        ),
                    data => this.mapDivisionConfigToFormData(data, this.divisionSalesforceTargetsForm)
                ),
            // Data for options retrieved on load
            this.#salesforceService.getObjects().pipe(map(objects => ({ salesforceObjectOptions: objects.sobjects }))),
            zip(this.selectedDivisionConfig$, this.#dataMartService.getDataModel()).pipe(
                map(([selectedDiv, dataModel]) => {
                    const { dataMartTable, dataMartTableOptions, dataMartFields } =
                        this.#formLifecycleService.configureDataMartData(selectedDiv, dataModel);
                    return {
                        dataMartTable,
                        dataMartTableOptions,
                        dataMartFields,
                    };
                })
            ),
            // Data retrieved as SF play or rec changes
            combineLatest([
                this.divisionSalesforceTargetsForm.controls.salesforceRecommendationObject.valueChanges,
                this.divisionSalesforceTargetsForm.controls.salesforcePlayObject.valueChanges,
            ]).pipe(
                switchMap(([sfRecObjValue, sfPlayObjValue]) => {
                    const calls = [];
                    if (sfRecObjValue) {
                        calls.push(this.#salesforceService.getFieldsForObject(sfRecObjValue));
                    } else {
                        calls.push(of());
                    }
                    if (sfPlayObjValue) {
                        calls.push(this.#salesforceService.getFieldsForObject(sfPlayObjValue));
                    } else {
                        calls.push(of());
                    }
                    return forkJoin(calls);
                }),
                map(([sfRecObjResponse]) => {
                    return {
                        recommendationFieldsOptions: sfRecObjResponse,
                    };
                })
            ),
        ],
    });

    public submit() {
        const state = this.salesforceTargetsFormState;

        const divisionId = this.#globalLoggedInUserStore.selectedDivisionConfig()?.id;
        const dataMartTableName = state().dataMartTable?.name;
        const existingMapping = state().selectedDivisionConfig?.config?.mapping;
        const loggedInUserId = this.loggedInUserId();

        let submitted: Observable<{ submitted: boolean }>;

        if (divisionId && dataMartTableName && existingMapping && loggedInUserId) {
            const uniqueSalesforceTargetFields = state().value.uniqueSalesforceTargetFields;

            const relationsAndCriteriaTypes$ = this.#formLifecycleService.getRelationsAndCrtieriaTypes(
                uniqueSalesforceTargetFields.length,
                uniqueSalesforceTargetFields.map(field => field.sourceField),
                dataMartTableName,
                (sourceTableName: string, sourceColumnName: string) =>
                    this.#dataMartService.getRelations(sourceTableName, sourceColumnName)
            );

            submitted = relationsAndCriteriaTypes$.pipe(
                switchMap(relationsAndCrtieriaTypes => {
                    const divisionConfig: RequiredDivisionConfigData = {
                        id: divisionId,
                        updatedBy: loggedInUserId,
                    };

                    const divisionSalesforceTargetsSettings: DivisionSalesforceTargetSettings =
                        this.formatMappingForSave(state(), relationsAndCrtieriaTypes.relationships);

                    const divisionConfigData: Pick<DivisionConfigData, 'mapping'> = {
                        mapping: {
                            ...existingMapping,
                            ...divisionSalesforceTargetsSettings,
                        },
                    };

                    return from(
                        this.#globalDivisionStore.saveDivisionConfigAndData({
                            config: divisionConfig,
                            data: divisionConfigData,
                        })
                    ).pipe(map(() => ({ submitted: true })));
                })
            );
        } else {
            submitted = of({ submitted: true });
        }
        return lastValueFrom(submitted);
    }

    constructor() {
        effect(
            () => {
                this.#formLifecycleService.enableOrDisableWhenLoading(
                    this.salesforceTargetsFormState.loaded(),
                    this.divisionSalesforceTargetsForm
                );
            },
            { debugName: 'form init + load' }
        );

        this.#validators.updateSalesForceTargetNameValidatorsOnOtherNamesChanging(
            this.divisionSalesforceTargetsForm.controls.uniqueSalesforceTargetFields,
            this.#destroyRef
        );
    }

    public addUnrequiredRow(
        uniqueSfTargetFieldsArr: FormArray<FormGroup<UniqueSalesforceTargetFieldForm>>,
        fb: NonNullableFormBuilder,
        validators: DivisionSalesforceTargetsFormValidatorsService
    ) {
        uniqueSfTargetFieldsArr.push(
            fb.group({
                id: fb.control(''),
                name: fb.control('', {
                    validators: validators.salesforceTargetTakenValidator(),
                }),
                sourceField: fb.control('', {
                    validators: [Validators.required],
                }),
                targetField: fb.control('', {
                    validators: [Validators.required],
                }),
            })
        );
    }

    /**
     * @description Step #1) Create empty form controls for X mapping fields of whatever type
     */
    private initializeEmptyMapperControls(
        data: DivisionConfig,
        uniqueSfTargetFieldsForm: FormArray<FormGroup<UniqueSalesforceTargetFieldForm>>,
        fb: NonNullableFormBuilder,
        validators: DivisionSalesforceTargetsFormValidatorsService
    ) {
        const uniqueSalesforceTargetFields = this.formatConfigMappingData(data) ?? [];

        const salesforceRecommendationTargetObjectsLength = uniqueSalesforceTargetFields.length;
        // The required mapping
        if (salesforceRecommendationTargetObjectsLength === 0) {
            uniqueSfTargetFieldsForm.push(
                fb.group({
                    id: fb.control(Uuid.generate()),
                    name: fb.control('', {
                        validators: [validators.salesforceTargetTakenValidator(), Validators.required],
                    }),
                    sourceField: fb.control('', {
                        validators: [Validators.required],
                    }),
                    targetField: fb.control('', {
                        validators: [Validators.required],
                    }),
                })
            );
        } else {
            for (let i = 0; i < salesforceRecommendationTargetObjectsLength; i++) {
                this.addUnrequiredRow(uniqueSfTargetFieldsForm, fb, validators);
            }
        }
    }

    /**
     * @description Step #1 and #2 helper
     */
    private formatConfigMappingData(divisionConfig: DivisionConfig) {
        const uniqueSFTargetFields = divisionConfig?.config?.mapping?.uniqueSalesforceTargetFields;

        return uniqueSFTargetFields && uniqueSFTargetFields?.length > 0
            ? divisionConfig?.config?.mapping?.uniqueSalesforceTargetFields?.map(field => ({
                  id: field.id,
                  name: field.name,
                  sourceField: field.sourceField.name,
                  targetField: field.targetField.name,
              }))
            : [];
    }

    /**
     * @description Step #2) Shape data to form slice state, and mark `loaded: true`
     */
    private mapDivisionConfigToFormData(
        divisionConfig: DivisionConfig,
        form: FormGroup<DivisionSalesforceTargetsForm>
    ): {
        value: Partial<DivisionSalesforceTargetsFormValue>;
        status: FormControlStatus;
        loaded: boolean;
    } {
        const uniqueSalesforceTargetFields = this.formatConfigMappingData(divisionConfig) ?? [];

        const dataMapping: DivisionMappingSettings | undefined = divisionConfig?.config?.mapping;

        const value: Partial<DivisionSalesforceTargetsFormSliceState['value']> = {
            salesforcePlayObject: dataMapping?.salesforcePlayObject,
            salesforceRecommendationObject: dataMapping?.salesforceRecommendationObject,
            salesforceUniqueRecommendationFieldTargetName: dataMapping?.salesforceUniqueRecommendationFieldTargetName,
            uniqueSalesforceTargetFields: uniqueSalesforceTargetFields,
        };
        const statusAndLoading: Pick<DivisionSalesforceTargetsFormSliceState, 'status' | 'loaded'> = {
            status: form.status,
            loaded: true,
        };

        return {
            value: value,
            status: statusAndLoading.status,
            loaded: statusAndLoading.loaded,
        };
    }

    /**
     * @description Maps over all the form data, derives respective server state, while
     *                  splitting up "both" fieldMapping into separate rows
     */
    private formatMappingForSave(
        state: DivisionSalesforceTargetsFormSliceState,
        divisionTableRelationships: DivisionTableRelationships[]
    ): DivisionSalesforceTargetSettings {
        const uniqueSalesforceTargetFields: SalesforceUniqueFieldTarget[] =
            state.value.uniqueSalesforceTargetFields.map((field, i) => {
                const salesforceFields = state.recommendationFieldsOptions?.fields ?? [];
                const sourceField: DataMartField | undefined = state.dataMartFields.find(dataMartField => {
                    return field.sourceField === dataMartField.name;
                });
                sourceField!.tableName = state.dataMartTable?.name;
                const targetFieldMetadata: SalesforceFieldMetadata | undefined = salesforceFields.find(
                    salesforceField => field.targetField === salesforceField.name
                );
                const targetField: SalesforceField = {
                    objectName: state.value.salesforceRecommendationObject,
                    label: targetFieldMetadata?.label ?? '',
                    type: targetFieldMetadata?.type ?? '',
                    name: targetFieldMetadata?.name ?? '',
                };
                return {
                    ...field,
                    sourceField: sourceField!,
                    targetField: targetField,
                    divisionTableRelationships: divisionTableRelationships[i],
                };
            });

        return {
            salesforcePlayObject: state.value.salesforcePlayObject,
            salesforceRecommendationObject: state.value.salesforceRecommendationObject,
            salesforceUniqueRecommendationFieldTargetName: state.value.salesforceUniqueRecommendationFieldTargetName,
            uniqueSalesforceTargetFields: uniqueSalesforceTargetFields,
        };
    }
}
